import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  Award,
  Users,
  Target,
  Sparkles,
  Star,
  Vote,
  Scale,
  Megaphone,
  Flag
} from "lucide-react";
import VSI from "../../assets/images/vidhansabhaInside.png";
import VSS from "../../assets/images/Vidhansabha_sitting.jpg";

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen overflow-hidden bg-white">
      {/* Big Container */}
      <div className="flex flex-col lg:flex-row min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 gap-8 sm:gap-12 lg:gap-16 items-center">
        {/* Left Container - Text */}
        <div className="w-full lg:w-1/2 flex items-center order-2 lg:order-1">
          <div className="w-full">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* Campaign Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-full"
              >
                <Vote className="w-4 h-4 text-orange-600 mr-2" />
                <span className="text-orange-700 text-sm font-semibold">Democracy Initiative</span>
              </motion.div>

              {/* Main Title */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight"
                >
                  <span className="text-gray-900">Youth in</span>
                  <br />
                  <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Politics</span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-lg"
              >
                Encouraging young leaders to actively participate in political processes, governance, and policy-making to ensure youth voices are heard.
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="flex flex-wrap gap-6 sm:gap-8 pt-4"
              >
                <div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                    Active
                  </div>
                  <div className="text-sm text-gray-600 font-medium">Candidates</div>
                </div>
                <div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                    Growing
                  </div>
                  <div className="text-sm text-gray-600 font-medium">Initiatives</div>
                </div>
                <div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                    Multiple
                  </div>
                  <div className="text-sm text-gray-600 font-medium">Offices</div>
                </div>
              </motion.div>

            </motion.div>
          </div>
        </div>

        {/* Right Container - Rounded Image */}
        <div className="w-full lg:w-1/2 flex items-center justify-center order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative w-full"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-red-600 to-orange-600 rounded-3xl opacity-20 blur-2xl"></div>
            <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src={VSS}
                alt="Youth political engagement and democracy"
                className="w-full h-full object-cover"
                style={{ objectPosition: '65% center' }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Our <span className="text-red-600">Mission</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Empowering young people to become active participants in democratic processes and effective agents of political change
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left - Mission Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Political Empowerment</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Equipping young people with the knowledge, skills, and confidence needed to engage effectively in political processes and run for public office.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Megaphone className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Policy Advocacy</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Training youth to effectively advocate for policies that matter to their generation and communities, from climate action to digital rights.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Scale className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Democratic Strengthening</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Fostering a new generation of leaders committed to democratic values, transparency, and inclusive governance.
                  </p>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="bg-red-50 rounded-2xl p-6 border border-red-200"
            >
              <h4 className="font-bold text-red-800 mb-3 flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                Vision Statement
              </h4>
              <p className="text-red-700 italic">
                "To create a political landscape where young voices are not just heard but actively shape policy decisions, ensuring that democracy remains vibrant, inclusive, and responsive to the needs of all generations."
              </p>
            </motion.div>
          </motion.div>

          {/* Right - Mission Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={VSI}
                alt="Young people in political engagement"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              
              {/* Overlay Stats */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30">
                  <div className="grid grid-cols-3 gap-4 text-center text-white">
                    <div>
                      <div className="text-2xl font-bold">78%</div>
                      <div className="text-xs">Youth Engaged</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">45+</div>
                      <div className="text-xs">Offices Won</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">93%</div>
                      <div className="text-xs">Policy Impact</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Element */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-6 -right-6 w-24 h-24 bg-red-500 rounded-full flex items-center justify-center shadow-xl"
            >
              <Vote className="w-8 h-8 text-white" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Impact Section
const ImpactSection = () => {
  return null;
};

// Main Youth in Politics Component
const YouthPoliticsInitiative = () => {
  useEffect(() => {
    document.title = "Youth in Politics Initiative - Harsh Sanghani";
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <HeroSection />
      <MissionSection />
      <ImpactSection />
    </div>
  );
};

export default YouthPoliticsInitiative;